import type { ReactNode } from 'react';
import Chip from '@mui/material/Chip';
import { STATUS_CHIP_CLASS, type StatusToken } from '@eis/theme';

/**
 * Maps a domain status onto the theme's status tokens.
 *
 * The map is the single place where "IN_NEGOTIATION is a pending-coloured thing" is decided, so
 * a Group Policy, a child-policy and a group-tariff version with the same lifecycle meaning always
 * look the same — including in the two invoice states, which are otherwise easy to colour ad hoc.
 */
const STATUS_TOKENS: Readonly<Record<string, StatusToken>> = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  TERMINATED: 'terminated',
  PENDING: 'pending',
  IN_NEGOTIATION: 'pending',
  PUBLISHED: 'published',
  // A discarded draft is closed for good, like a terminated contract — and unlike a draft.
  DISCARDED: 'terminated',
  // A retired version (SDD-016 D-V3): stood down for new business, still resolving for every
  // existing pin — suspended, not ended.
  RETIRED: 'suspended',
  PAID: 'active',
  OPEN: 'pending',
  // The billing vocabulary (SDD-015 §5.1, §5.6): an invoice's statuses, a schedule row's, a run's.
  'PARTIALLY-PAID': 'pending',
  REFUNDED: 'terminated',
  ESTIMATED: 'draft',
  PAUSED: 'suspended',
  INVOICED: 'active',
  VIA_MASTER: 'published',
  WITHDRAWN: 'terminated',
  RUNNING: 'pending',
  SUCCEEDED: 'active',
  PARTIAL: 'suspended',
  // A failed run is an error, not an ending (T-31: the one mapping change, its own `danger` tone).
  FAILED: 'danger',
  // A background job (SDD-020 §6.2): waiting is pending, a stop on request is not an error, and
  // an expired job's file is gone for good.
  QUEUED: 'pending',
  CANCELLED: 'suspended',
  EXPIRED: 'terminated',
};

/**
 * A semantic tone, for a status vocabulary this map does not own (claimservice's statuses carry
 * their tone in claimservice.default-statuses.json, SDD-018 §9): info is the published blue,
 * warning the draft amber, success the active teal, neutral the suspended grey.
 */
export type StatusTone = 'info' | 'warning' | 'success' | 'neutral';

const TONE_TOKENS: Readonly<Record<StatusTone, StatusToken>> = {
  info: 'published',
  warning: 'draft',
  success: 'active',
  neutral: 'suspended',
};

export interface StatusChipProps {
  /** Domain status as it arrives from the API, e.g. `ACTIVE`, `IN_NEGOTIATION`. */
  readonly status: string;
  /** Translated label. Never pass a raw domain constant to the user. */
  readonly label: string;
  readonly size?: 'small' | 'medium';
  /** Draws this tone instead of the one the status map names (a foreign status vocabulary). */
  readonly tone?: StatusTone;
}

export function statusTokenFor(status: string): StatusToken {
  return STATUS_TOKENS[status.toUpperCase()] ?? 'draft';
}

export function StatusChip({ status, label, size = 'small', tone }: StatusChipProps): ReactNode {
  const token = tone === undefined ? statusTokenFor(status) : TONE_TOKENS[tone];
  return (
    <Chip
      size={size}
      label={label}
      // The theme draws its own shape on this class — design v2's tonal chip in berry.
      className={STATUS_CHIP_CLASS}
      // Status is also announced in text, so colour is never the only carrier of meaning. The
      // colours are the scheme's CSS variables, not `theme.palette` values: with CSS variables on,
      // the palette object is the LIGHT scheme, and the dark chips read light colours before.
      sx={{
        color: `var(--mui-palette-status-${token}-main)`,
        backgroundColor: `var(--mui-palette-status-${token}-surface)`,
        border: 1,
        borderColor: `var(--mui-palette-status-${token}-border)`,
      }}
    />
  );
}
